import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Caption1,
  Field,
  MessageBar,
  MessageBarBody,
  Select,
  Text,
  Title3,
  makeStyles,
} from '@fluentui/react-components';
import { NOT_READY_UPLOAD_WORDING, findStepType, isNotReadyUpload, portalFormCount, portalFormHeader, portalFormPosition, rejectionSentenceFor } from '@vigeo/shared';
import { tokens } from '@vigeo/ui';
import { PortalStepRenderer, findStepUi, type UploadResult } from '@/steps';
import { isElsewhere, isOutstanding, wasSentByCandidate } from './outstanding.js';
import { SubmitHint } from './SubmitHint.js';
import {
  isPortalError,
  loadTemplateBytes,
  saveDraft,
  snapshotPacket,
  submitStep,
  uploadFile,
  type PacketStep,
  type PortalError,
} from './api.js';
import { clearLocalDraft, readLocalDraft, reconcile, writeLocalDraft } from './drafts.js';

const useStyles = makeStyles({
  runner: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  head: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS },
  nav: { display: 'flex', gap: tokens.spacingHorizontalS, flexWrap: 'wrap' },
  button: { minHeight: '2.75rem' },
  status: { minHeight: tokens.lineHeightBase200 },
  selector: { width: '100%', minWidth: 0, maxWidth: '100%', '& select': { minHeight: '2.75rem' } },
});

/** Long enough not to save every keystroke, short enough to survive a dropped connection. */
const DEBOUNCE_MS = 1500;

export interface StepRunnerProps {
  readonly steps: readonly PacketStep[];
  readonly onExpired: (error: PortalError) => void;
}

/**
 * Is this draft a complete answer?
 *
 * The draft and the submission are the SAME shape by design: a step component edits toward
 * the submission rather than into a parallel structure that something later has to
 * translate. So "can this be submitted" is just the type's own submissionSchema run against
 * the draft — no per-type knowledge here, which is what keeps this runner from growing a
 * switch statement over step types.
 *
 * The server re-runs the identical check and is the actual gate. This one only decides
 * whether a button is enabled.
 */
function isComplete(step: PacketStep, draft: unknown): boolean {
  const kernel = findStepType(step.type);
  if (kernel === undefined) return false;
  // The TYPE decides what "complete" means. For most, the draft simply is the submission. For
  // one whose submission the server completes — pdf-form, where the server fills and stages
  // the PDF — requiring the draft to satisfy the submission schema would disable the button
  // forever with nothing on screen explaining why.
  return kernel.runtime.readyToSubmit(step.config, draft);
}

/**
 * One step at a time.
 *
 * The counter is `3 of 7` — a COUNT, never a percentage and never a readiness verdict. A
 * candidate is doing the things they were asked for; they are not being scored, and a
 * progress bar that stalls at 86% because of an unsettled agency interval would be both
 * confusing and none of their business.
 */
/**
 * What we already have, as plain text.
 *
 * Deliberately NOT rendered through PortalStepRenderer. Passing `disabled` to a step's own
 * component still draws its controls — an approved document still showed a "Choose a file"
 * button — and a disabled control reads as something you are meant to act on but cannot,
 * which is worse than no control at all.
 *
 * It is shown rather than hidden because "did they get my licence?" is the question this
 * portal exists to stop someone phoning about.
 */
function ReceivedList({ steps, notReady }: { readonly steps: readonly PacketStep[]; readonly notReady: (step: PacketStep) => boolean }) {
  const styles = useStyles();
  if (steps.length === 0) return null;
  return (
    <div className={styles.head}>
      <Caption1>What we have from you</Caption1>
      {steps.map((step) => (
        <Text key={step.id}>
          {/* A "not ready" answer is not a document we hold (#232), so it never reads as received. */}
          {step.heading} — {notReady(step) ? NOT_READY_UPLOAD_WORDING.listed : step.state === 'approved' ? 'accepted' : 'received, with our office'}
        </Text>
      ))}
    </div>
  );
}

/**
 * What is happening somewhere else.
 *
 * Rendered through each step's own portal component, unlike `ReceivedList` — and the reason
 * the two differ is the reason that comment gives. A finished document's component would draw
 * a disabled file chooser; a step in this section draws a status and nothing to press, because
 * an office-owned type with a portal half exists precisely to say "here is where this stands,
 * there is nothing for you to do". The e2e matrix asserts the section offers no control at any
 * width, so a future type that breaks that promise fails rather than quietly asking a candidate
 * for something the server would refuse.
 */
function ElsewhereList({ steps }: { readonly steps: readonly PacketStep[] }) {
  const styles = useStyles();
  if (steps.length === 0) return null;
  return (
    <section className={styles.head} aria-label="Happening elsewhere">
      <Caption1>Being handled elsewhere</Caption1>
      {steps.map((step) => (
        <div key={step.id} className={styles.head}>
          <Text weight="semibold">{step.heading}</Text>
          <PortalStepRenderer
            type={step.type}
            config={step.config}
            status={step.status}
            draft={null}
            onDraftChange={NOTHING}
            disabled
            upload={NO_UPLOAD}
          />
        </div>
      ))}
    </section>
  );
}

/** This section is read-only by construction: neither capability does anything. */
const NOTHING = () => {};
const NO_UPLOAD = async () => ({ ok: false as const, message: 'Nothing is uploaded here.' });

export function StepRunner({ steps: packet, onExpired }: StepRunnerProps) {
  const styles = useStyles();
  const [index, setIndex] = useState(0);
  const [draft, setDraft] = useState<unknown>(null);
  const [draftStepId, setDraftStepId] = useState<string | null>(null);
  const [revision, setRevision] = useState(-1);
  const [restoredFrom, setRestoredFrom] = useState<'server' | 'device' | 'none'>('none');
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [navigating, setNavigating] = useState(false);
  const [availability, setAvailability] = useState<{ stepId: string; available: boolean } | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  /** Which steps this session has sent. The packet from the server is not re-fetched. */

  const [submitted, setSubmitted] = useState<ReadonlySet<string>>(new Set());
  /** Of those, the ones answered "not ready" (#232). The packet is not re-fetched, so it cannot say. */
  const [sentNotReady, setSentNotReady] = useState<ReadonlySet<string>>(new Set());
  const notReady = (s: PacketStep) => submitted.has(s.id) ? sentNotReady.has(s.id) : s.state === 'submitted' && s.uploadNotReady;

  // Only what they still have to do. The QA finding was a completed portal reading
  // "1 of 23" — counting finished work and office-recorded items as outstanding tasks.
  const outstanding = packet.filter((s) => isOutstanding(s) && !submitted.has(s.id));
  /**
   * Work that CAME BACK goes first, and this is not a nicety.
   *
   * A candidate asked to correct an item lands on it first. The selector still lets them
   * choose any other outstanding item, including a newly issued offer or controlled form.
   *
   * A stable sort, so everything else keeps the order the packet gave it.
   */
  const steps = [...outstanding].sort((a, b) => Number(b.state === 'rejected') - Number(a.state === 'rejected'));
  /** What they have already sent, kept visible as reassurance rather than as a task. */
  const received = packet.filter((s) => wasSentByCandidate(s) || submitted.has(s.id));
  /** Theirs to care about, nobody's to do here: an obligation in flight in another system. */
  const elsewhere = packet.filter((s) => isElsewhere(s, (type) => findStepUi(type)?.portal != null));

  const step = steps[index];
  // Draft writes and autosave status must not restart an in-progress PDF render.
  const packetTemplate = step?.template;
  const template = useMemo(() => packetTemplate == null ? undefined : {
    version: packetTemplate.version,
    load: () => loadTemplateBytes(packetTemplate.bytesUrl),
  }, [packetTemplate]);
  const stepId = step?.id;
  const onAvailabilityChange = useCallback((available: boolean) => {
    if (stepId) setAvailability({ stepId, available });
  }, [stepId]);
  const previewAvailable = template === undefined || (availability !== null && availability.stepId === stepId && availability.available);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pending = useRef<{ value: unknown; revision: number } | null>(null);

  // Load whichever copy is newer when the step changes.
  useEffect(() => {
    if (step === undefined) return;
    const merged = reconcile(
      step.draft === null ? null : { value: step.draft.value, revision: step.draft.revision },
      readLocalDraft(step.id),
    );
    setDraft(merged.value);
    setDraftStepId(step.id);
    setRevision(merged.revision);
    setRestoredFrom(merged.from);
    setSavedAt(null);
    setProblem(null);
  }, [step]);

  const flush = useCallback(async () => {
    const queued = pending.current;
    const current = step;
    if (queued === null || current === undefined) return;
    pending.current = null;

    setSaving(true);
    const result = await saveDraft(current.id, queued.value, queued.revision);
    setSaving(false);

    if (isPortalError(result)) {
      // The session died mid-form. Everything typed stays on screen and in sessionStorage;
      // losing a long form on a phone is the exact support call this portal exists to remove.
      if (result.status === 401 || result.status === 429) {
        writeLocalDraft(current.id, { value: queued.value, revision: queued.revision });
        onExpired(result);
      }
      return;
    }
    setSavedAt(result.savedAt);
  }, [step, onExpired]);

  // Flush on the way out. mobile Safari discards a backgrounded tab without ever firing
  // `beforeunload`, so `visibilitychange` is the only reliable last chance.
  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === 'hidden') void flush();
    };
    document.addEventListener('visibilitychange', onHide);
    return () => document.removeEventListener('visibilitychange', onHide);
  }, [flush]);

  function change(value: unknown) {
    if (step === undefined) return;
    const next = revision + 1;
    setDraft(value);
    setRevision(next);
    // Written locally FIRST, so a save that never lands still leaves the work recoverable.
    writeLocalDraft(step.id, { value, revision: next });
    pending.current = { value, revision: next };

    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = setTimeout(() => void flush(), DEBOUNCE_MS);
  }

  async function chooseStep(next: number) {
    if (submitting || saving || navigating || next === index || next < 0 || next >= steps.length) return;
    setNavigating(true);
    if (timer.current !== null) clearTimeout(timer.current);
    try {
      // Finish this step's queued save before the closure points at another step. Local
      // recovery remains available even when the server save cannot complete.
      await flush();
      setIndex(next);
    } finally { setNavigating(false); }
  }

  /**
   * The upload capability handed to whichever component is rendered. The instance id is
   * closed over here, so no step component ever handles one.
   */
  const upload = useCallback(
    async (file: File, onProgress: (fraction: number) => void): Promise<UploadResult> => {
      if (step === undefined) return { ok: false, message: 'Nothing to upload to.' };
      const result = await uploadFile(step.id, file, onProgress);
      if (isPortalError(result)) {
        if (result.status === 401 || result.status === 429) onExpired(result);
        return { ok: false, message: result.message };
      }
      return { ok: true, ...result };
    },
    [step, onExpired],
  );

  async function submit() {
    if (step === undefined) return;
    setProblem(null);
    setSubmitting(true);

    // Any queued autosave goes first. Submitting while a draft save is still in flight would
    // let the save land afterwards and reopen a step that has already been sent.
    if (timer.current !== null) clearTimeout(timer.current);
    await flush();

    const failed = await submitStep(step.id, draft);
    setSubmitting(false);

    if (failed !== null) {
      if (failed.status === 401 || failed.status === 429) {
        onExpired(failed);
        return;
      }
      setProblem(failed.message);
      return;
    }

    // Only now is the local copy discarded — it is superseded by something attested.
    clearLocalDraft(step.id);
    if (isNotReadyUpload(draft)) setSentNotReady(new Set(sentNotReady).add(step.id));
    const done = new Set(submitted);
    done.add(step.id);
    setSubmitted(done);

    if (steps.every((s) => done.has(s.id))) {
      // Seal the packet. A failure here is invisible on purpose: the person's submissions
      // are already stored, and the snapshot is an internal record they cannot act on.
      await snapshotPacket();
      return;
    }
    // Deliberately NOT advancing. A submitted step leaves the outstanding list, so the next
    // one slides into this index by itself — incrementing here would step over it.
    setIndex((i) => Math.min(i, steps.length - 2));
  }

  if (step === undefined) {
    return (
      <div className={styles.runner}>
        <MessageBar intent="success">
          <MessageBarBody>There is nothing left for you to do right now.</MessageBarBody>
        </MessageBar>
        <ElsewhereList steps={elsewhere} />
        <ReceivedList steps={received} notReady={notReady} />
      </div>
    );
  }

  if (steps.length === 0) {
    // The completion state. Previously the portal kept rendering the full packet — every
    // finished step and every office-recorded item — so somebody who had done everything
    // still saw a long list of apparent tasks with a file chooser on the first one.
    return (
      <div className={styles.runner}>
        <MessageBar intent="success">
          <MessageBarBody>
            That is everything — thank you. Our office will review it and be in touch if
            anything else is needed.
          </MessageBarBody>
        </MessageBar>
        {/* Shown HERE above all: a candidate told "that is everything" while an obligation of
            theirs is still outstanding elsewhere has been told something misleading. */}
        <ElsewhereList steps={elsewhere} />
        <ReceivedList steps={received} notReady={notReady} />
      </div>
    );
  }

  const rejectionSentence = rejectionSentenceFor(step.rejectionReason);
  /** HR sent the secure upload link for a "not ready" answer (#232): asked again, not rejected. */
  const reopenedForUpload = step.state === 'rejected' && step.uploadNotReady;
  /*
    THE FORMS THAT ARE THEIRS — the packet minus what is happening elsewhere, and that exclusion is
    load-bearing rather than tidiness. An I-9 handled in QuickBooks sits in the packet but is not a
    task the candidate can do, and a test already guards that it "is not counted as one of their
    tasks". Counting the raw packet put it back in the total, telling somebody with one form to fill
    in that they had two. Caught by that test, which is why it is still worded that way.

    Position over that list, not among what is OUTSTANDING: a form's number must not change because
    the form before it was submitted. It counts within the forms the person ACTUALLY has, which is
    Paul's decision of 2026-09-30 — his frames number against a finished portal of 22, only 13 of
    which exist today. See `portalFormPosition`.
  */
  const forms = packet.filter((item) => !isElsewhere(item, (type) => findStepUi(type)?.portal != null));
  const position = portalFormPosition(forms, step.id);
  /*
   * `portalFormCount`, not `forms.length`: one numbered form may hold several steps, so once
   * anything is grouped the step count and the form count differ — and the total is the number a
   * candidate counts against. `forms.length` would say "Form 3 of 13" on a packet of 9 forms.
   * Identical to `forms.length` while nothing is grouped, which is the case today.
   */
  const header = position === null ? null : portalFormHeader({ number: position, total: portalFormCount(forms), type: step.type });
  const draftLoaded = draftStepId === step.id;
  const ready = draftLoaded && isComplete(step, draft);

  return (
    <div className={styles.runner}>
      <div className={styles.head}>
        {/*
          "Form 4 of 22 · Read and sign" (#372 part 3, Paul 2026-09-29/30) — the form's place in the
          WHOLE packet, which is why it reads off `packet` rather than `steps`.

          IT REPLACES "3 of 7", and that was a counter over OUTSTANDING work: a number that shrank
          as the candidate worked, and that the note at the top of this file records a QA finding
          against. This is an identity instead — form 4 is form 4 whether it is done or not — so the
          two cannot be blended. The frames show one line, not both.

          Null when the step is not in the packet it was drawn from, which the header refuses rather
          than printing "Form 0 of 22" over somebody's signature page.
        */}
        {header === null ? null : <Caption1>{header}</Caption1>}
        <Title3>{step.heading}</Title3>
        {step.helpText === '' ? null : <Text>{step.helpText}</Text>}
        {steps.length > 1 ? <Field label="Choose a step" className={styles.selector}>
          <Select className={styles.selector} value={step.id} disabled={submitting || saving || navigating} onChange={(_, data) => { void chooseStep(steps.findIndex((item) => item.id === data.value)); }}>
            {steps.map((item) => <option key={item.id} value={item.id}>{item.heading}{item.state === 'rejected' ? item.uploadNotReady ? ' — requested by our office' : ' — needs correction' : ''}</option>)}
          </Select>
        </Field> : null}
      </div>

      {rejectionSentence === null ? null : (
        <MessageBar intent="warning">
          {/* The stored value is a CODE. Rendering it raw told a candidate whose document
              came back that the problem was, in full, "unreadable". The sentence resolves
              from the shared presenter registry — the same one the HR reviewer previews
              before sending, so the two can never say different things. */}
          <MessageBarBody>
            {rejectionSentence}
            {/* HR's own words, as plain text in a text node — never as HTML. */}
            {step.candidateComment === '' ? null : <><br /><span>A note from our office: {step.candidateComment}</span></>}
          </MessageBarBody>
        </MessageBar>
      )}

      {reopenedForUpload ? (
        <MessageBar intent="info">
          <MessageBarBody>{NOT_READY_UPLOAD_WORDING.reopened}</MessageBarBody>
        </MessageBar>
      ) : null}

      {restoredFrom === 'device' ? (
        <MessageBar intent="info">
          <MessageBarBody>We brought back what you had typed on this device.</MessageBarBody>
        </MessageBar>
      ) : null}

      {problem === null ? null : (
        <MessageBar intent="error">
          <MessageBarBody>{problem}</MessageBarBody>
        </MessageBar>
      )}

      {/* Restore this step before mounting its editor. A PDF editor seeds unanswered
          checkboxes on mount; it must see the saved answers before doing that. */}
      {draftLoaded ? <PortalStepRenderer
        key={step.id}
        type={step.type}
        config={step.config}
        draft={draft}
        onDraftChange={change}
        disabled={submitting || navigating}
        upload={upload}
        template={template}
        onAvailabilityChange={onAvailabilityChange}
      /> : <Text>Loading your saved answers…</Text>}

      <div className={styles.status}>
        <Caption1>
          {saving ? 'Saving…' : savedAt === null ? '' : 'Saved. You can close this and come back.'}
        </Caption1>
      </div>

      <div className={styles.nav}>
        <Button
          className={styles.button}
          disabled={index === 0 || submitting || saving || navigating}
          onClick={() => { void chooseStep(index - 1); }}
        >
          Back
        </Button>
        <Button
          className={styles.button}
          appearance="primary"
          disabled={!ready || submitting || saving || navigating || !previewAvailable}
          onClick={() => void submit()}
        >
          {submitting ? 'Sending…' : 'Send this to our office'}
        </Button>
      </div>

      <SubmitHint ready={ready} previewAvailable={previewAvailable} />

      {/* Below the work, not among it: an obligation they must act on somewhere else is not
          one of the steps this screen is counting. Kept visible while there is still work to
          do, so nobody learns about their I-9 only after finishing everything else. */}
      <ElsewhereList steps={elsewhere} />
    </div>
  );
}

export { clearLocalDraft };
